import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { z } from "zod";
import { trpcClient } from "@/integrations/tanstack-query/root-provider";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";

const searchSchema = z.object({
	token: z.string().optional(),
	redirect: z.string().optional(),
});

export const Route = createFileRoute("/vendor-auth")({
	validateSearch: searchSchema,
	component: VendorAuthPage,
});

// Lazy-load supabase client only on the client side
async function getSupabase() {
	if (typeof window === "undefined") return null;
	const { supabase } = await import("@/lib/supabase-browser");
	return supabase;
}

function VendorAuthPage() {
	const navigate = useNavigate();
	const { token, redirect } = Route.useSearch();
	const [status, setStatus] = useState<"loading" | "error" | "success">(
		"loading",
	);
	const [errorMessage, setErrorMessage] = useState<string>("");

	useEffect(() => {
		const authenticateVendor = async () => {
			if (!token) {
				setStatus("error");
				setErrorMessage("No authentication token provided");
				return;
			}

			try {
				// Exchange Bidscope token for purchasing portal session
				const result = await trpcClient.vendorAuth.exchangeToken.mutate({
					accessToken: token,
				});

				// Set the session on the Supabase client
				const supabase = await getSupabase();
				if (supabase) {
					await supabase.auth.setSession({
						access_token: result.accessToken,
						refresh_token: result.refreshToken,
					});
				}

				setStatus("success");

				// Redirect to vendor portal or specified redirect URL
				setTimeout(() => {
					if (redirect) {
						navigate({ to: redirect });
					} else {
						// Default redirect to vendor portal with first available buyer
						navigate({ to: "/vendor/bidscope" });
					}
				}, 1000);
			} catch (error) {
				console.error("Vendor auth error:", error);
				setStatus("error");
				setErrorMessage(
					error instanceof Error
						? error.message
						: "Authentication failed. Please try again.",
				);
			}
		};

		authenticateVendor();
	}, [token, redirect, navigate]);

	return (
		<div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
			<Card className="w-full max-w-md">
				<CardHeader className="space-y-1">
					<CardTitle className="text-2xl font-bold text-center">
						Vendor Portal Access
					</CardTitle>
					<CardDescription className="text-center">
						{status === "loading" && "Authenticating your account..."}
						{status === "success" && "Authentication successful!"}
						{status === "error" && "Authentication failed"}
					</CardDescription>
				</CardHeader>
				<CardContent className="flex flex-col items-center justify-center min-h-[200px]">
					{status === "loading" && (
						<div className="flex flex-col items-center gap-4">
							<Loader2 className="h-8 w-8 animate-spin text-primary" />
							<p className="text-muted-foreground">
								Please wait while we verify your credentials...
							</p>
						</div>
					)}

					{status === "success" && (
						<div className="flex flex-col items-center gap-4">
							<div className="h-12 w-12 rounded-full bg-green-100 flex items-center justify-center">
								<svg
									className="h-6 w-6 text-green-600"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										strokeWidth={2}
										d="M5 13l4 4L19 7"
									/>
								</svg>
							</div>
							<p className="text-muted-foreground">
								Redirecting to vendor portal...
							</p>
						</div>
					)}

					{status === "error" && (
						<div className="flex flex-col items-center gap-4">
							<div className="h-12 w-12 rounded-full bg-red-100 flex items-center justify-center">
								<svg
									className="h-6 w-6 text-red-600"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										strokeWidth={2}
										d="M6 18L18 6M6 6l12 12"
									/>
								</svg>
							</div>
							<p className="text-red-600 text-center">{errorMessage}</p>
							<div className="flex gap-2 mt-4">
								<Button
									variant="outline"
									onClick={() => window.location.reload()}
								>
									Try Again
								</Button>
								<Button
									onClick={() =>
										(window.location.href = "https://app.bidscope.ai")
									}
								>
									Return to Bidscope
								</Button>
							</div>
						</div>
					)}
				</CardContent>
			</Card>
		</div>
	);
}
